import { useRef, useCallback } from 'react';
import { Stack, Text, Group, Button } from '@mantine/core';
import { useModal } from './useModal';

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * Опасное (деструктивное) действие. Инвертирует акценты кнопок:
   * «Отмена» становится primary (синяя filled) — безопасный дефолт,
   * «Подтвердить» — красная filled, чтобы пользователь осознанно подтвердил.
   */
  danger?: boolean;
}

type ConfirmContentProps = {
  message: string;
  options: ConfirmOptions;
  onResult: (value: boolean) => void;
};

/**
 * Содержимое модалки подтверждения.
 */
function ConfirmContent({ message, options, onResult }: ConfirmContentProps) {
  const cancelProps = options.danger
    ? { variant: 'filled' as const, color: 'blue' }
    : { variant: 'light' as const, color: 'gray' };
  const confirmProps = options.danger
    ? { variant: 'filled' as const, color: 'red' }
    : { variant: 'filled' as const, color: 'blue' };

  return (
    <Stack gap='md'>
      <Text size='sm'>{message}</Text>
      <Group justify='flex-end' gap='sm'>
        <Button {...cancelProps} size='xs' onClick={() => onResult(false)}>
          {options.cancelLabel ?? 'Отмена'}
        </Button>
        <Button {...confirmProps} size='xs' onClick={() => onResult(true)}>
          {options.confirmLabel ?? 'Подтвердить'}
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * useConfirm — хук для показа диалога подтверждения.
 *
 * Построен поверх useModal — модалка рендерится через глобальный ModalHost,
 * не требует вставки node в JSX.
 *
 * Возвращает функцию `confirm(message, options?)` — Promise<boolean>.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const confirm = useConfirm();
 *
 *   const handleArchive = async () => {
 *     const ok = await confirm('Сделка имеет документы. Продолжить?', {
 *       title: 'Архивирование',
 *       confirmLabel: 'Архивировать',
 *     });
 *     if (!ok) return;
 *     await doArchive();
 *   };
 *
 *   return <Button onClick={handleArchive}>Архивировать</Button>;
 * }
 * ```
 */
export function useConfirm(): (message: string, options?: ConfirmOptions) => Promise<boolean> {
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const modal = useModal<ConfirmContentProps>(ConfirmContent);

  modal.onClose(() => {
    resolveRef.current?.(false);
    resolveRef.current = null;
  });

  const confirm = useCallback(
    (message: string, options?: ConfirmOptions): Promise<boolean> => {
      return new Promise<boolean>(resolve => {
        resolveRef.current = resolve;

        const onResult = (value: boolean) => {
          resolveRef.current = null;
          resolve(value);
          modal.close();
        };

        modal.open(
          { title: options?.title ?? 'Подтверждение', message, options: options ?? {}, onResult },
          { size: 'xs' }
        );
      });
    },
    [modal]
  );

  return confirm;
}
